import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { endOfDay, endOfHour, sub } from 'date-fns';
import {
  EventWidget,
  WidgetWrapper,
  getAllEventCounts,
  useWidgetsContext,
} from '@logspot-platform/widgets';
import type { DateRange, EventData, EventDataMap } from '@logspot-platform/widgets';
import { Button } from '@logspot-platform/ui/button';
import {
  DATE_RANGE_PRESETS,
  DEFAULT_PRESET_INDEX,
} from './lib/date-ranges';
import { execute } from './lib/execute';
import type { EmbedWidgetConfig } from './lib/default-widgets';

const EmbedWidget = ({
  widget,
  dateRange,
  prevDateRange,
  eventData,
  prevEventData,
}: {
  widget: EmbedWidgetConfig;
  dateRange: DateRange;
  prevDateRange: DateRange;
  eventData: EventDataMap;
  prevEventData: EventDataMap;
}) => {
  const title = widget.params?.title ?? widget.params?.eventName ?? widget.type;
  const params = { ...widget.params, title };

  if (widget.type === 'EVENT') {
    const eventName = widget.params?.eventName ?? title;
    return (
      <EventWidget
        data={eventData[eventName] ?? []}
        dateRange={dateRange}
        name={title}
        prevData={prevEventData[eventName] ?? []}
      />
    );
  }

  // `mapFunction` names a host-page function (loader `mapping` option) used
  // to map raw property values — proxied over postMessage RPC.
  const mapFunction =
    widget.type === 'AGGREGATION_BY_PROPERTY'
      ? widget.params?.mapFunction
      : undefined;

  return (
    <WidgetWrapper
      dateRange={dateRange}
      internal
      params={params}
      prevDateRange={prevDateRange}
      transformData={
        mapFunction
          ? (data: EventData[]) =>
              Promise.all(
                data.map(async (entry) => {
                  const mapped = await execute(mapFunction, [entry.property]);
                  return {
                    ...entry,
                    property: mapped == null ? undefined : String(mapped),
                  };
                }),
              )
          : undefined
      }
      transformKey={mapFunction}
      type={widget.type}
    />
  );
};

export const Dashboard = ({ widgets }: { widgets: EmbedWidgetConfig[] }) => {
  const { api, projectId, filterGroups } = useWidgetsContext();
  const [presetIndex, setPresetIndex] = useState(DEFAULT_PRESET_INDEX);
  const [dateRange, setDateRange] = useState<DateRange>(
    () => DATE_RANGE_PRESETS[DEFAULT_PRESET_INDEX]!.range(),
  );

  const prevDateRange = {
    interval: dateRange.interval,
    startDate: sub(dateRange.startDate, dateRange.period || { days: 1 }),
    endDate: dateRange.interval.includes('days')
      ? endOfDay(dateRange.startDate)
      : endOfHour(dateRange.startDate),
  };

  const eventsQuery = useQuery({
    queryKey: ['events-all:count', projectId, dateRange, filterGroups],
    queryFn: () =>
      getAllEventCounts(api, {
        projectId,
        interval: dateRange.interval,
        startDate: dateRange.startDate.getTime(),
        endDate: dateRange.endDate.getTime(),
        filterGroups,
      }),
  });
  const prevEventsQuery = useQuery({
    queryKey: ['events-all:count:previous', projectId, dateRange, filterGroups],
    queryFn: () =>
      getAllEventCounts(api, {
        projectId,
        interval: prevDateRange.interval,
        startDate: prevDateRange.startDate.getTime(),
        endDate: prevDateRange.endDate.getTime(),
        filterGroups,
      }),
  });

  return (
    <section className="mx-auto w-full max-w-5xl flex-1 p-5 md:p-10">
      <div className="flex flex-wrap items-center gap-2">
        {DATE_RANGE_PRESETS.map((preset, index) => (
          <Button
            key={preset.label}
            size="sm"
            variant={index === presetIndex ? 'primary' : 'outline'}
            onClick={() => {
              setPresetIndex(index);
              setDateRange(preset.range());
            }}
          >
            {preset.label}
          </Button>
        ))}
      </div>
      <div className="mt-5 mb-10 grid grid-cols-1 flex-wrap gap-4 sm:grid-cols-2 md:gap-6">
        {widgets.map((widget, index) => (
          <EmbedWidget
            key={`${widget.type}_${index}`}
            dateRange={dateRange}
            eventData={eventsQuery.data ?? {}}
            prevDateRange={prevDateRange}
            prevEventData={prevEventsQuery.data ?? {}}
            widget={widget}
          />
        ))}
      </div>
      <footer className="flex justify-center pb-8">
        <a
          className="flex items-center gap-2 font-bold text-foreground"
          href="https://logspot.io"
          rel="noreferrer"
          target="_blank"
        >
          Powered by <span className="text-primary">Logspot</span>
        </a>
      </footer>
    </section>
  );
};
